<template>
  <artisio-modal :visible="show" :title="title" body-class="qr-code-modal" size="lg" hide-footer
                 :content-in-form="false" @hidden="hideModal" modal-class="mobile-full-screen">
    <spinner :show="loading" :height="33"/>
    <div class="qr-scanner-container" v-show="!loading">
      <b-form-select
        v-model="dropdownValue"
        :options="dropdownOptions"
        class="camera-switcher"
        size="sm"
      ></b-form-select>
      <qrcode-stream
        v-if="streamEnabled"
        :key="`camera-${streamKey}`"
        :constraints="streamConstraints"
        @decode="onDecode"
        @init="onInit"
        @camera-on="onCameraOn"
      />
    </div>
  </artisio-modal>
</template>

<script>
  import ArtisioModal from "../artisio-modal/ArtisioModal";
  import {ON_QR_CODE_READER_MODAL_OPEN} from "../../../plugins/event-bus/event-bus-names";
  import Spinner from "../common/Spinner";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";
  import QrcodeStream from "@/core/components/qr-code-generator/QrcodeStream";

  export default {
    name: "QrCodeReader",
    components: {Spinner, ArtisioModal, QrcodeStream},
    data() {
      // Matching native script exactly
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      return {
        show: false,
        loading: false,
        title: null,
        error: '',
        handler: null,
        isMobile: isMobile,
        isIOS: isIOS,
        selectedCamera: isMobile ? 'rear' : 'auto',
        cameraOptions: [
          { value: 'auto', text: this.$i18n ? this.$i18n.t('Auto') : 'Auto' },
          { value: 'rear', text: this.$i18n ? this.$i18n.t('Rear Camera') : 'Rear Camera' },
          { value: 'front', text: this.$i18n ? this.$i18n.t('Front Camera') : 'Front Camera' }
        ],
        availableDevices: [],
        selectedDeviceId: null,
        cameraReady: false,
        streamEnabled: true,
        restartToken: 0,
        useAutoFallback: false
      }
    },
    computed: {
      // Matching native script exactly
      dropdownOptions() {
        if (this.isIOS) {
          return this.cameraOptions;
        }
        if (this.cameraReady && this.availableDevices.length > 0) {
          return this.availableDevices.map(device => ({
            value: device.deviceId,
            text: device.label || `Camera ${device.deviceId.substring(0, 8)}`
          }));
        }
        return this.cameraOptions;
      },
      dropdownValue: {
        get() {
          if (this.isIOS) {
            return this.selectedCamera;
          }
          return this.cameraReady && this.selectedDeviceId ? this.selectedDeviceId : this.selectedCamera;
        },
        set(value) {
          if (this.isIOS) {
            if (value !== this.selectedCamera) {
              this.selectedCamera = value;
              this.useAutoFallback = false;
              this.restartStream();
            }
            return;
          }
          if (this.cameraReady) {
            if (value !== this.selectedDeviceId) {
              this.selectedDeviceId = value;
              this.useAutoFallback = false;
              this.restartStream();
            }
          } else {
            if (value !== this.selectedCamera) {
              this.selectedCamera = value;
              this.useAutoFallback = false;
              this.restartStream();
            }
          }
        }
      },
      streamKey() {
        const cameraKey = this.isIOS ? this.selectedCamera : (this.selectedDeviceId || this.selectedCamera);
        return `${cameraKey}-${this.restartToken}`;
      },
      streamConstraints() {
        // Matching native script's streamConstraints exactly
        if (this.isIOS) {
          return {
            video: {
              facingMode: { exact: this.selectedCamera === 'front' ? 'user' : 'environment' }
            }
          };
        }
        if (this.useAutoFallback) {
          return { video: true };
        }
        if (this.cameraReady && this.selectedDeviceId) {
          return { video: { deviceId: { exact: this.selectedDeviceId } } };
        }
        if (this.selectedCamera === 'front') {
          return { video: { facingMode: 'user' } };
        }
        return { video: { facingMode: 'environment' } };
      }
    },
    methods: {
      restartStream() {
        if (!this.streamEnabled) {
          return;
        }
        this.streamEnabled = false;
        this.$nextTick(() => {
          this.restartToken += 1;
          this.streamEnabled = true;
        });
      },
      buildReaderInitErrorMessage(error) {
        const baseMessage = QrCodeService.getReaderInitializationError(error)
          || (error && error.message)
          || this.$i18n.t('Something went wrong while initializing camera.');
        const details = [];

        if (error && typeof error === 'object') {
          if (error.name) {
            details.push(`Name: ${error.name}`);
          }
          if (error.message && error.message !== baseMessage) {
            details.push(`Message: ${error.message}`);
          }
          if (error.constraint) {
            details.push(`Constraint: ${error.constraint}`);
          }
          if (error.code) {
            details.push(`Code: ${error.code}`);
          }
        } else if (error) {
          details.push(String(error));
        }

        const h = this.$createElement;
        const children = [h('div', {class: ['mb-2']}, [baseMessage])];

        if (details.length) {
          children.push(h('div', {class: ['small', 'text-muted']}, details.map((detail, index) => (
            h('div', {key: index}, [detail])
          ))));
        }

        return h('div', children);
      },
      hideModal() {
        this.title = null;
        this.handler = null;
        this.error = '';
        this.show = false;
        this.loading = false;
        this.selectedCamera = this.isMobile ? 'rear' : 'auto';
        this.selectedDeviceId = null;
        this.availableDevices = [];
        this.cameraReady = false;
        this.streamEnabled = true;
        this.restartToken = 0;
        this.useAutoFallback = false;
      },
      onDecode(result) {
        this.$vibrate();
        this.handler(result);
        this.hideModal();
      },
      async onInit(promise) {
        // Matching native script's onInit exactly
        try {
          this.loading = true;
          await promise;
          this.loading = false;

          if (!this.isIOS) {
            await this.onCameraOn();
          }
        } catch (error) {
          if (!this.isIOS && !this.useAutoFallback && !this.selectedDeviceId) {
            this.useAutoFallback = true;
            this.restartStream();
            return;
          }
          this.$errorDialog(this.buildReaderInitErrorMessage(error));
          this.hideModal();
        }
      },
      async onCameraOn(mediaStream) {
        // Matching native script's onCameraOn exactly
        if (this.cameraReady) {
          if (mediaStream && !this.isIOS) {
            this.syncSelectedDeviceId(mediaStream);
          }
          return;
        }
        if (this.isIOS) {
          return;
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(device => device.kind === 'videoinput');

          if (videoDevices.length === 0) {
            return;
          }

          this.availableDevices = videoDevices;

          const rearCamera = videoDevices.find(device =>
            /back|rear|environment|trasera|arrière|posteriore/i.test(device.label)
          );

          const frontCamera = videoDevices.find(device =>
            /front|user|selfie|delantera|avant|frontale/i.test(device.label)
          );

          if (this.isMobile && rearCamera) {
            this.selectedDeviceId = rearCamera.deviceId;
          } else if (frontCamera && !this.isMobile) {
            this.selectedDeviceId = frontCamera.deviceId;
          } else {
            this.selectedDeviceId = videoDevices[0].deviceId;
          }

          this.cameraReady = true;
          if (mediaStream) {
            this.syncSelectedDeviceId(mediaStream);
          }
        } catch (error) {
          console.error('Error enumerating devices:', error);
        }
      },
      syncSelectedDeviceId(mediaStream) {
        if (!mediaStream || !mediaStream.getVideoTracks) {
          return;
        }
        const [track] = mediaStream.getVideoTracks();
        if (!track || !track.getSettings) {
          return;
        }
        const settings = track.getSettings();
        if (settings && settings.deviceId) {
          const match = this.availableDevices.find(device => device.deviceId === settings.deviceId);
          if (match) {
            this.selectedDeviceId = match.deviceId;
          }
        }
      }
    },
    beforeMount() {
      this.show = false;
      this.$evBus.$on(ON_QR_CODE_READER_MODAL_OPEN, ({title, handler}) => {
        this.title = title || this.$i18n.t('Scan QR Code');
        this.handler = handler;
        this.show = true;
      });
    },
    beforeDestroy() {
      this.$evBus.$off(ON_QR_CODE_READER_MODAL_OPEN);
    },
  }
</script>

<style scoped>
  .error {
    font-weight: bold;
    color: red;
  }

  .qr-scanner-container {
    position: relative;
  }

  .camera-switcher {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    width: auto;
    max-width: 150px;
    opacity: 0.9;
    font-size: 0.875rem;
  }

  .camera-switcher:hover {
    opacity: 1;
  }

  @media (max-width: 576px) {
    .camera-switcher {
      font-size: 0.75rem;
      max-width: 120px;
    }
  }
</style>
